/**
 * Normalizuje databázový záznam objednávky do camelCase tvaru.
 * Zachová pôvodné kľúče (snake_case), aby zostali kompatibilné s existujúcim kódom.
 *
 * @param {object|null|undefined} orderRow - Radek objednávky načítaný zo Supabase.
 * @returns {object|null} Normalizovaná objednávka alebo null pri neplatnom vstupe.
 */
export const calculateOrderPricing = (ticketCountInput, ticketPriceInput = 5) => {
  const ticketCount = Math.max(Number(ticketCountInput) || 0, 0);
  const ticketPrice = Math.max(Number(ticketPriceInput) || 0, 0);
  const freeTickets = Math.floor(ticketCount / 6); // Akcia: kúp 5, 1 zdarma.
  const payableTickets = Math.max(ticketCount - freeTickets, 0);
  const totalAmount = payableTickets * ticketPrice;

  return {
    ticketCount,
    ticketPrice,
    freeTickets,
    payableTickets,
    totalAmount,
  };
};

export const normalizeOrder = (orderRow) => {
  if (!orderRow || typeof orderRow !== 'object') {
    return null;
  }

  const tickets = Array.isArray(orderRow.tickets) ? orderRow.tickets : [];
  const ticketCount =
    typeof orderRow.ticket_count === 'number'
      ? orderRow.ticket_count
      : typeof orderRow.ticketCount === 'number'
      ? orderRow.ticketCount
      : tickets.length;

  const fallbackPricing = calculateOrderPricing(ticketCount, 5);
  const totalAmount =
    typeof orderRow.total_amount === 'number'
      ? orderRow.total_amount
      : typeof orderRow.totalAmount === 'number'
      ? orderRow.totalAmount
      : fallbackPricing.totalAmount;

  return {
    ...orderRow,
    firstName: orderRow.first_name ?? orderRow.firstName ?? '',
    lastName: orderRow.last_name ?? orderRow.lastName ?? '',
    email: orderRow.email ?? '',
    phone: orderRow.phone ?? '',
    city: orderRow.city ?? '',
    status: orderRow.status ?? 'pending',
    tickets,
    ticketCount,
    totalAmount,
    qrCode: orderRow.qr_code ?? orderRow.qrCode ?? '',
    createdAt: orderRow.created_at ?? orderRow.createdAt ?? null,
    updatedAt: orderRow.updated_at ?? orderRow.updatedAt ?? null,
    createdAtFormatted: orderRow.created_at
      ? formatDateTime(orderRow.created_at)
      : orderRow.createdAtFormatted ?? '',
    updatedAtFormatted: orderRow.updated_at
      ? formatDateTime(orderRow.updated_at)
      : orderRow.updatedAtFormatted ?? '',
  };
};

/**
 * Pomocná funkcia na zistenie, či normalizovaná objednávka obsahuje pripravený QR kód.
 *
 * @param {object|null} order - Normalizovaná objednávka.
 * @returns {boolean} True, ak je QR kód k dispozícii.
 */
export const hasQrCode = (order) => {
  return Boolean(order?.qrCode && order.qrCode.trim().length > 0);
};

/**
 * Formátuje dátum na jednoduchý slovenský formát.
 *
 * @param {string|Date} value - Hodnota dátumu.
 * @returns {string} Formátovaný dátum alebo prázdny reťazec.
 */
const formatDateTime = (value) => {
  if (!value) {
    return '';
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleString('sk-SK', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
